import React from "react";
import styled from "@emotion/styled";
import {getLeaderboardUserByUserId, getPlayerByIdSelector} from "modules/selectors";
import {SectionTitle} from "./styles";
import {IUserProfile} from "modules/actions/leaderboards";
import {useSelector} from "react-redux";
import {PlayerImageType} from "modules";
import {PlayerImage} from "components/Player/PlayerElements/PlayerAvatar";
import UserInfoRow from "pages/homePage/userProfile/userInfo/UserInfoRow";
import {Exist} from "components";
import {useTranslation} from "react-i18next";

const Wrapper = styled.div`
	width: 100%;
	max-width: 300px;
	display: flex;
	gap: 16px;
	flex-flow: column;
	position: sticky;
	top: 0;
	@media (max-width: 1100px) {
		order: 1;
		max-width: 100%;
		position: static;
	}
`;

const InnerContainer = styled.div`
	display: flex;
	flex-flow: column;
`;

const Stats = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	& > div {
		display: flex;
		flex-flow: column;
		padding: 12px 8px 8px;
		justify-content: center;
		align-items: center;
		gap: 4px;
		background: linear-gradient(90deg, #42474d 0%, #282f38 100%);
		border-radius: 8px;
		flex: 1;
		min-width: 40%;
		span {
			font-weight: 700;
			font-size: 20px;
			line-height: 120%;
		}

		p {
			font-weight: 400;
			font-size: 16px;
			line-height: 1.1;
			text-align: center;
		}

		@media (max-width: 880px) {
			min-width: 30%;
			span {
				font-size: 18px;
			}
			p {
				font-size: 14px;
			}
		}
	}
`;

const FavouritePlayer = styled.div`
	padding-right: 8px;
	align-items: center;
	background: linear-gradient(90deg, #42474d 0%, #282f38 100%);
	border-radius: 8px;
	display: flex;
	margin-top: 20px;
	justify-content: space-between;

	img {
		width: 110px;
		height: auto;
		margin-top: -18px;
	}
`;

const FavouriteContent = styled.div`
	font-size: 16px;
	display: flex;
	flex-flow: column;
	gap: 4px;
	line-height: 1.2;
	padding: 10px 0;
	margin-right: auto;
	p {
		&:first-of-type {
			font-size: 20px;
			font-weight: 700;
			line-height: 1.3;
			@media (max-width: 880px) {
				font-size: 18px;
			}
		}
	}

	@media (max-width: 880px) {
		font-size: 14px;
	}
`;

const Picks = styled.div`
	font-size: 12px;
	display: flex;
	flex-flow: column;
	font-weight: 400;
	line-height: 1;
	align-items: center;
	justify-content: center;
	padding: 16px 10px;
	min-width: 60px;
	min-height: 65px;
	background: #121212;
	border-radius: 6px;
	p {
		&:first-of-type {
			font-size: 20px;
			font-weight: 700;
			line-height: 1.3;
		}
	}
`;

interface IProps {
	user: ReturnType<ReturnType<typeof getLeaderboardUserByUserId>>;
	userId: number;
	isMobile: boolean;
	profile?: IUserProfile;
}

const IS_HIDDEN_FAV_SECTION = true;
const UserInfo: React.FC<IProps> = ({user, userId, isMobile, profile}) => {
	const {i18n} = useTranslation();
	const playersSelector = useSelector(getPlayerByIdSelector);

	if (!profile) {
		return null;
	}

	const {favouriteBatter, seasonBestStreak, activeStreak, accuracy} = profile;

	const favouritePlayer = playersSelector(favouriteBatter?.playerId);

	return (
		<Wrapper>
			<SectionTitle>{i18n.t("user_profile.stats.title", "Season Long Stats")}</SectionTitle>

			<InnerContainer>
				<Exist when={!isMobile}>
					<UserInfoRow user={user} userId={userId} isMobile={false} />
				</Exist>

				<Stats>
					<div aria-labelledby={"season-best-title"}>
						<span>{seasonBestStreak}</span>
						<p id={"season-best-title"}>
							{i18n.t("user_profile.stats.best_streak", "Best Streak")}
						</p>
					</div>
					<div aria-labelledby={"active-streak-title"}>
						<span>{activeStreak}</span>
						<p id={"active-streak-title"}>
							{i18n.t("user_profile.stats.active_streak", "Active Streak")}
						</p>
					</div>
					<div aria-labelledby={"accuracy-title"}>
						<span>{accuracy ? `${profile.accuracy}%` : "-"}</span>
						<p id={"accuracy-title"}>
							{i18n.t("user_profile.stats.pick_accuracy", "Pick Accuracy")}
						</p>
					</div>
				</Stats>

				{favouritePlayer && !IS_HIDDEN_FAV_SECTION && (
					<FavouritePlayer>
						<PlayerImage type={PlayerImageType.headshot} player={favouritePlayer} />
						<FavouriteContent>
							<p>{favouritePlayer.name}</p>
							<p>{i18n.t("user_profile.stats.favorite_batter", "Favorite Batter")}</p>
						</FavouriteContent>

						<Picks>
							<p>{favouriteBatter?.selectionsCount}</p>
							<p>{i18n.t("user_profile.stats.picks", "Picks")}</p>
						</Picks>
					</FavouritePlayer>
				)}
			</InnerContainer>
		</Wrapper>
	);
};

export default UserInfo;
